import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { triggerLogout } from '../services/auth';
import {
  setProfileData,
  setProfileLoading,
  setOnboardingStatus,
  setRedeemedToken,
  selectCountry,
  selectOrganizationId,
  selectOnboardingStatus,
  selectRedeemedToken,
  selectHasFilledProfile,
  selectHasSetupLocation,
  selectSelectedLocation,
  selectSelectedOrganization,
  selectIsProfileLoading,
  selectIsInitialized,
  selectIsProfileComplete,
  selectNeedsOnboarding,
  setSelectedLocation,
  setSelectedOrganization,
  resetAppState,
  ONBOARDING_STATUS,
} from '../store/slices/appSlice';

/**
 * Central hook for application state management.
 * Consolidates profile fetching, language switching, and state access.
 * Replaces prop drilling in ProtectedRoute.
 */
export const useAppState = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();
  const { i18n } = useTranslation();

  // Redux selectors
  const country = useSelector(selectCountry);
  const organizationId = useSelector(selectOrganizationId);
  const onboardingStatus = useSelector(selectOnboardingStatus);
  const redeemedToken = useSelector(selectRedeemedToken);
  const hasFilledProfile = useSelector(selectHasFilledProfile);
  const hasSetupLocation = useSelector(selectHasSetupLocation);
  const selectedLocation = useSelector(selectSelectedLocation);
  const selectedOrganization = useSelector(selectSelectedOrganization);
  const isProfileLoading = useSelector(selectIsProfileLoading);
  const isInitialized = useSelector(selectIsInitialized);
  const isProfileComplete = useSelector(selectIsProfileComplete);
  const needsOnboarding = useSelector(selectNeedsOnboarding);

  // Country to language mapping (using BCP 47 language codes)
  // Currently only supporting Canada
  const countryLanguageMap = {
    Canada: 'en-CA',
  };

  // Fetch profile status from API
  const fetchProfileStatus = useCallback(async () => {
    if (!isAuthenticated || !user?.sub) {
      dispatch(setProfileLoading(false));
      return;
    }

    dispatch(setProfileLoading(true));

    try {
      const token = await getAccessTokenSilently();
      const response = await apiClient.get(`/api/users/profile/${user.sub}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;

      // Map backend onboarding_status to frontend constant
      // Backend returns: 'profile_pending', 'token_pending', 'org_setup', 'complete'
      const statusMap = {
        profile_pending: ONBOARDING_STATUS.PROFILE_PENDING,
        token_pending: ONBOARDING_STATUS.TOKEN_PENDING,
        org_setup: ONBOARDING_STATUS.ORG_SETUP,
        complete: ONBOARDING_STATUS.COMPLETE,
      };

      // Determine onboarding status from API response
      // Profile API returns { userData: { onboarding_status, ... }, hasFilledProfile, hasStoreLocationSetup }
      const onboardingStatusFromApi = data.userData?.onboarding_status || data.onboarding_status;
      let derivedStatus;

      if (data.hasFilledProfile && data.hasStoreLocationSetup) {
        // Definitive: if profile + location both exist, user is complete.
        // This handles legacy users where Mongoose applies default 'profile_pending'
        // even though they completed onboarding before this field was added.
        derivedStatus = ONBOARDING_STATUS.COMPLETE;
      } else if (onboardingStatusFromApi) {
        derivedStatus = statusMap[onboardingStatusFromApi] || ONBOARDING_STATUS.PROFILE_PENDING;
      } else if (!data.hasFilledProfile) {
        derivedStatus = ONBOARDING_STATUS.PROFILE_PENDING;
      } else {
        derivedStatus = ONBOARDING_STATUS.TOKEN_PENDING;
      }

      dispatch(
        setProfileData({
          onboardingStatus: derivedStatus,
          country: data.userData?.country || '',
          organizationId: data.userData?.organization_id || null,
        })
      );
    } catch (error) {
      console.error('[useAppState] Failed to fetch profile status:', error);

      // Auth errors (expired refresh token, 401) mean the session is gone —
      // log the user out instead of dropping them into onboarding.
      const isAuthError =
        error?.error === 'login_required' ||
        error?.error === 'invalid_grant' ||
        error?.error === 'consent_required' ||
        error?.error === 'missing_refresh_token' ||
        error?.response?.status === 401;

      if (isAuthError) {
        dispatch(resetAppState());
        triggerLogout();
        return;
      }

      dispatch(
        setProfileData({
          onboardingStatus: ONBOARDING_STATUS.PROFILE_PENDING,
          country: '',
          organizationId: null,
        })
      );
    }
  }, [isAuthenticated, user?.sub, getAccessTokenSilently, dispatch]);

  // Fetch profile on auth change
  useEffect(() => {
    if (isAuthenticated && user && !isInitialized) {
      fetchProfileStatus();
    } else if (!isAuthenticated) {
      dispatch(setProfileLoading(false));
    }
  }, [isAuthenticated, user, isInitialized, fetchProfileStatus, dispatch]);

  // Change language when country changes
  useEffect(() => {
    const language = countryLanguageMap[country];
    if (language) {
      i18n.changeLanguage(language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, i18n]);

  // Use refs to track current values and avoid stale closures
  const selectedLocationRef = useRef(selectedLocation);
  const selectedOrganizationRef = useRef(selectedOrganization);

  // Keep refs in sync with Redux state
  useEffect(() => {
    selectedLocationRef.current = selectedLocation;
  }, [selectedLocation]);

  useEffect(() => {
    selectedOrganizationRef.current = selectedOrganization;
  }, [selectedOrganization]);

  // Listen for localStorage changes (cross-tab sync)
  useEffect(() => {
    const handleStorageChange = () => {
      const newLocation = localStorage.getItem('selectedLocation') || '';
      const newOrg = localStorage.getItem('selectedOrganization') || '';

      // Compare against refs to avoid stale closure issues
      if (newLocation !== selectedLocationRef.current) {
        dispatch(setSelectedLocation(newLocation));
      }
      if (newOrg !== selectedOrganizationRef.current) {
        dispatch(setSelectedOrganization(newOrg));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('storageEvent', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storageEvent', handleStorageChange);
    };
  }, [dispatch]);

  // Actions
  const updateSelectedLocation = useCallback(
    (location) => {
      dispatch(setSelectedLocation(location));
      // Update ref immediately to prevent stale comparison in event handler
      selectedLocationRef.current = location;
    },
    [dispatch]
  );

  const updateSelectedOrganization = useCallback(
    (org) => {
      dispatch(setSelectedOrganization(org));
      // Update ref immediately to prevent stale comparison in event handler
      selectedOrganizationRef.current = org;
    },
    [dispatch]
  );

  const resetState = useCallback(() => {
    dispatch(resetAppState());
  }, [dispatch]);

  const refetchProfile = useCallback(() => {
    return fetchProfileStatus();
  }, [fetchProfileStatus]);

  // Action to update onboarding status (used after completing each step)
  const updateOnboardingStatus = useCallback(
    (status) => {
      dispatch(setOnboardingStatus(status));
    },
    [dispatch]
  );

  // Action to store redeemed token data
  const updateRedeemedToken = useCallback(
    (tokenData) => {
      dispatch(setRedeemedToken(tokenData));
    },
    [dispatch]
  );

  return {
    // State
    country,
    organizationId,
    onboardingStatus,
    redeemedToken,
    hasFilledProfile,
    hasSetupLocation,
    selectedLocation,
    selectedOrganization,
    isProfileLoading,
    isInitialized,
    isProfileComplete,
    needsOnboarding,

    // Actions
    updateSelectedLocation,
    updateSelectedOrganization,
    updateOnboardingStatus,
    updateRedeemedToken,
    resetState,
    refetchProfile,

    // Constants (for convenience)
    ONBOARDING_STATUS,
  };
};

export default useAppState;
